/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isNumber from 'lodash.isnumber';
import isUndefined from 'lodash.isundefined';
import {translate} from '../locale/locale';
import {
  DeviceBulkEditorFormFields
} from '../types/editors';
import {
  AccountLink,
  CTCEntityResponseWrapper,
  Device,
  Entity
} from '../types/entities';
import {
  BulkOperationResults,
  ExtendedResults
} from '../types/services';
import {
  EntityTemplate,
  TemplateColumn,
  TemplateOperators,
  TemplateValidation
} from '../types/templates';
import {formatIfValid} from '../utils/DateUtil';
import {Mapped} from '../utils/ObjectUtil';
import {
  createValidation,
  formatErrors,
  statusColumn
} from './TemplateHelpers';

// defines the type definitions for device template options ...
export type DeviceTemplateOptions = {
  groupName?: string;
};

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to Device imports for download by the User.
 *
 * Device Template column definitions are defined as localized entries within
 * the admin-devices.json localization documents for each respective locale.
 * Entries take the following form:
 *
 * ```typescript
 *
 * import {devicesTemplate} from '@ion/core/src/xlsx/DeviceTemplate';
 *
 * const templates = {
 *   [Entity.device] : devicesTemplate
 * };
 * ```
 *
 * @param columns An array of localized device template column definitions.
 * @param options An array of other optional column definitions.
 */
export const devicesTemplate = ({columns, workbook, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[] = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {
    [DeviceBulkEditorFormFields.Status] : statusColumn
  };
  columns.forEach((column, index) => {
    const {key, type, required, conditionallyRequired, max, min, error, width, style} = column;
    const validation = createValidation({required, conditionallyRequired, error, index, type});
    const {header} = column;
    if (error) {
      if (isNumber(max) && required) {
        validation.formulae = [1, max];
        validation.operator = TemplateOperators.Between;
      } else if (isNumber(max)) {
        validation.formulae = [max];
        validation.operator = TemplateOperators.LessThanOrEqual;
      } else if (isNumber(min)) {
        validation.formulae = [min];
        validation.operator = TemplateOperators.GreaterThanOrEqual;
      }
    }
    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, validation, workbook, options}) || validation);
  });
  return {
    columnEntries,
    columnValidations
  };
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Devices import for download by the user.
 *
 * ```typescript
 *
 * import {getDeviceImportErrors} from '@ion/core/src/xlsx/DeviceTemplate';
 *
 * const processedResult = getDeviceImportErrors(invalidDevices, result);
 * ```
 *
 * @param invalidDevices Devices which are invalid to be imported.
 * @param result Response returned from server.
 *
 * @returns The processed results with errors if any
 */
export function getDeviceImportErrors(
  invalidDevices: CTCEntityResponseWrapper<Device>[],
  result: ExtendedResults,
  group: AccountLink
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatErrors(result?.errors, Entity.device);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidDevices?.length) {
    invalidDevices.forEach(({device}: any) => {
      let message;
      const {account}   = device;
      const accountName = group?.title;
      const groupName   = account?.title || (device.groupName || device.parentGroup || accountName);
      if (device.groupName || device.parentGroup) {
        message = translate('admin-devices:import.errors.invalidGroup', {
          device : device.name,
          group  : groupName,
          parent : accountName
        });
      } else if (!device.account) {
        message = translate('admin-devices:import.errors.noGroup', {
          device : device.name
        });
      } else if (device.type) {
        message = translate('admin-devices:import.errors.invalidType', {
          device : device.name,
          type   : device.type,
          group  : groupName
        });
      } else if (device.deviceConfiguration) {
        message = translate('admin-devices:import.errors.invalidConfiguration', {
          device        : device.name,
          configuration : device.deviceConfiguration,
          type          : device.type
        });
      } else {
        message = translate('admin-devices:import.errors.invalidESN', {
          device : device.name
        });
      }
      result?.errors?.push({
        error : message,
        name  : device.name,
        message,
        device
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which maps the results against the template columns
 * before the same is available as excel for user to download.
 *
 * ```typescript
 *
 * import {exportMapper} from '@ion/core/src/xlsx/DeviceTemplate';
 *
 * const processedResult = exportMapper(invalidDevices, result);
 * ```
 *
 * @param results Response from server.
 * @param boolean Checks if response has errors only.
 * @param Mapped Map which helps in mapping the results against respective
 * template columns.
 *
 * @returns The results mapped against the respective columns for generating
 * excel data.
 */
export function exportMapper(
  {results, errors}: BulkOperationResults | ExtendedResults,
  errorsOnly: boolean,
  attributes: Mapped
): unknown[] {
  const entries      = [];
  const items        = errorsOnly ? errors : results.concat(errors);
  const succeeded    = translate('common:succeeded');
  const failed       = translate('common:failed');
  const unavailable  = translate('common:notAvailable');
  items?.forEach(item => {
    const entry = [];
    const isGlobalError = !isUndefined(item.code);
    const hasErrors = !isUndefined(item.error) || isGlobalError;
    const result = item?.task;
    Object.values(attributes)?.forEach((attribute: string) => {
      if (!isGlobalError && result) {
        const val = result[attribute];
        switch (attribute) {
          case 'id' :
            entry.push((val));
            break;
          case 'groupName':
            entry.push(result.account ? result.account.title : result.groupName);
            break;
          case 'type':
            entry.push(result.type ? result.type.title : unavailable);
            break;
          case 'deviceConfiguration':
            entry.push(result.deviceConfiguration ? result.deviceConfiguration.title : unavailable);
            break;
          case 'shipDate' :
            entry.push(formatIfValid(val));
            break;
          default :
            entry.push(val);
            break;
        }
      } else {
        entry.push(unavailable);
      }
    });
    entry.push(hasErrors ? failed : succeeded);
    entry.push(hasErrors ? (!isGlobalError ? item.error : item.message) : '');
    entries.push(entry);
  });
  return entries;
};
